import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { PriceListUerRatioRow } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { IconAlert, Informer } from 'uikit';

import type { GetRowIdParams } from 'ag-grid-community/dist/types/core/interfaces/iCallbackParams';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useUerApplyPriceListUerRatioRows } from '../hooks/useUerApplyPriceListUerRatioRows';
import { useUerPriceListRatioRowsWithScopes } from '../hooks/useUerPriceListRatioRowsWithScopes';
import { useUerPriceListRatioRowsWithScopesColumns } from '../hooks/useUerPriceListRatioRowsWithScopesColumns';

import Style from './RatioRowsWithScopesSelectorModal.module.css';

interface IRatioRowsWithScopesSelectorModal {
    onClose: (selectedIds?: number[]) => void;
    uerBaseRowId: number;
    ratioType: RatioTypeEnum;
    contractId: number;
}

const style = { width: 1280 };

const getRowId = (params: GetRowIdParams<PriceListUerRatioRow>) => String(params.data.id);

const title = 'Добавление коэффициента из прейскуранта коэффициентов';

export const RatioRowsWithScopesSelectorModal: React.FC<IRatioRowsWithScopesSelectorModal> = (props) => {
    const { onClose, uerBaseRowId, ratioType, contractId } = props;

    const [ratioRowIds, setRatioRowIds] = useState<number[]>([]);

    const onCheckChange = useCallback((checkedIds: number[]) => setRatioRowIds(checkedIds), []);

    const onSuccess = useCallback(() => {
        onClose(ratioRowIds);
    }, [onClose, ratioRowIds]);

    const { dataSource, changeFilterHandler } = useUerPriceListRatioRowsWithScopes({
        uerBaseRowId,
        ratioType,
        contractId,
    });

    const columns = useUerPriceListRatioRowsWithScopesColumns({
        checkedIds: ratioRowIds,
        onCheckChange,
        uerBaseRowId,
        contractId,
        changeFilterHandler,
    });

    const { uerApplyPriceListUerRatioRowsStatus, uerApplyPriceListUerRatioRowsMakeRequest } =
        useUerApplyPriceListUerRatioRows({
            uerBaseRowId,
            onSuccess,
        });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const onConfirm = useCallback(() => {
        uerApplyPriceListUerRatioRowsMakeRequest(ratioRowIds);
    }, [ratioRowIds, uerApplyPriceListUerRatioRowsMakeRequest]);

    const onCloseLocal = useCallback(() => {
        if (!ratioRowIds.length) {
            onClose();
        } else {
            handleOpenConfirmationDialog();
        }
    }, [handleOpenConfirmationDialog, onClose, ratioRowIds.length]);

    const btnConfirmDisabled = useMemo(() => !ratioRowIds.length, [ratioRowIds.length]);

    return (
        <>
            <ModalDialog
                title={title}
                isOpen
                hasOverlay
                onClose={onCloseLocal}
                onConfirm={onConfirm}
                btnConfirmLabel="Добавить"
                btnCancelLabel="Отменить"
                withTitleCloseButton
                style={style}
                btnConfirmDisabled={btnConfirmDisabled}
                isCommit={uerApplyPriceListUerRatioRowsStatus === RequestStatus.FETCHING}
                isOnClickOutsideDisable
            >
                <div className={Style.ratioRowsWithScopesSelectorBody}>
                    {ratioType === RatioTypeEnum.DismantlingCoef && (
                        <Informer
                            status="system"
                            size="s"
                            icon={IconAlert}
                            className={Style.ratioRowsWithScopesSelectorInformer}
                        >
                            При добавлении коэффициентов на демонтаж для расценки будет присвоен вид работ
                            &quot;Строительные и специальные строительные работы&quot;
                        </Informer>
                    )}
                    <div
                        className={
                            ratioType === RatioTypeEnum.DismantlingCoef
                                ? Style.ratioRowsWithScopesSelectorTableBodyDismantling
                                : Style.ratioRowsWithScopesSelectorTableBody
                        }
                    >
                        <AgGridTable
                            getRowId={getRowId}
                            datasource={dataSource}
                            columnDefs={columns}
                            rowHeight={40}
                            rowModelType="infinite"
                            maxConcurrentDatasourceRequests={1}
                            maxBlocksInCache={50}
                            cacheBlockSize={50}
                        />
                    </div>
                </div>
            </ModalDialog>
            <ConfirmationDialog
                textHeader={title}
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

RatioRowsWithScopesSelectorModal.displayName = 'RatioRowsWithScopesSelectorModal';
